// /firebaseConfig.ts

import { initializeApp, getApps, getApp } from "firebase/app";
import { connectAuthEmulator, getAuth, initializeAuth, browserLocalPersistence, browserSessionPersistence, indexedDBLocalPersistence, setPersistence } from "firebase/auth";
import { clearIndexedDbPersistence, connectFirestoreEmulator, initializeFirestore, memoryLocalCache, setLogLevel } from "firebase/firestore";
import { isOrganizationAccess } from './src/common/organizationAccessMode';
import { isManagementPortal } from './src/common/siteEnvironment';
import { connectFunctionsEmulator, getFunctions } from "firebase/functions";
import { getAnalytics, setAnalyticsCollectionEnabled } from "firebase/analytics";
import { connectStorageEmulator, getStorage } from "firebase/storage";

export const firebaseConfig = {
    apiKey: import.meta.env.VITE_FIREBASE_API_KEY || '',
    authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || '',
    projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || '',
    storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '',
    appId: import.meta.env.VITE_FIREBASE_APP_ID || '',
    measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID || '',
};

// INITIALIZE INSTANCES
const useEmulators = import.meta.env.VITE_USE_EMULATORS === 'true';
if (useEmulators && (!firebaseConfig.projectId.startsWith('demo-') || !['localhost', '127.0.0.1'].includes(window.location.hostname)))
    throw new Error('Local emulators require a demo project and a loopback browser address.');
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();
const auth = isOrganizationAccess
    ? initializeAuth(app, { persistence: browserSessionPersistence })
    : isManagementPortal
        ? initializeAuth(app, { persistence: [browserSessionPersistence, browserLocalPersistence, indexedDBLocalPersistence] })
        : getAuth(app);
// Migrate old management logins out of durable browser storage.
const authSessionReady = isManagementPortal ? setPersistence(auth, browserSessionPersistence) : Promise.resolve();
const functions = getFunctions(app);
const db = initializeFirestore(app, { localCache: memoryLocalCache() });
// Drop documents cached by older builds before any Firestore reads begin.
const legacyPersistenceCleanup = clearIndexedDbPersistence(db);
// Startup handles failure; attach now in case auth migration fails first.
void legacyPersistenceCleanup.catch(() => undefined);
const analytics = useEmulators ? undefined : getAnalytics(app);
const storage = getStorage(app);
if (useEmulators) {
    connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true });
    connectFirestoreEmulator(db, '127.0.0.1', 8081);
    connectFunctionsEmulator(functions, '127.0.0.1', 5001);
    connectStorageEmulator(storage, '127.0.0.1', 9199);
}

// GPC PRIVACY LOGIC
if (analytics && typeof navigator !== 'undefined' && (navigator as any).globalPrivacyControl === true) {
    setAnalyticsCollectionEnabled(analytics, false);
}

setLogLevel('error');

// CRITICAL: Ensure these names match your imports in App.tsx and other components
export { app, auth, db, functions, analytics, storage, authSessionReady, legacyPersistenceCleanup, isManagementPortal };
